import { LitElement, html } from "lit";
import { customElement, property } from "lit/decorators.js";
import type { ContentPayload } from "../types";
import { completedModules } from "../services/progress";

@customElement("home-page")
export class HomePage extends LitElement {
  @property({ attribute: false }) content!: ContentPayload;
  protected createRenderRoot() { return this; }

  render() {
    const completed = completedModules();
    const playable = this.content.catalog.studies.filter((study) => study.status === "playable");
    return html`
      <main>
        <section class="hero home-hero">
          <div class="eyebrow">An interactive history of metal in motion</div>
          <h1>Hear what makes<br><em>metal move.</em></h1>
          <p class="lede">Start with a kick and snare. End with fifty years of blast beats, double bass, impossible-feeling groupings, and the drummers who made them musical.</p>
          <div class="hero-actions">
            <a class="button primary" href="/learn/meet-the-kit">Start from scratch</a>
            <a class="button" href="/graph">Explore the evolution</a>
          </div>
          <div class="hero-stats">
            <span><b>06</b> fundamentals</span><span><b>30</b> performances</span><span><b>47</b> years</span><span><b>04</b> drum approaches per riff</span>
          </div>
        </section>

        <section class="section course-section">
          <div class="section-heading">
            <div><span class="section-number">01</span><h2>Learn to hear the kit</h2></div>
            <p>Thirty to forty-five minutes. No notation. No prior drumming knowledge.</p>
          </div>
          <div class="module-list">
            ${this.content.course.modules.map((module) => html`
              <a class="module-card ${completed.has(module.slug) ? "complete" : ""}" href="/learn/${module.slug}">
                <span class="module-number">0${module.number}</span>
                <span class="module-copy"><b>${module.title}</b><small>${module.summary}</small></span>
                <span class="module-time">${completed.has(module.slug) ? "Complete" : module.duration}</span>
                <span class="arrow">↗</span>
              </a>
            `)}
          </div>
        </section>

        <section class="section graph-callout">
          <div class="graph-orbit" aria-hidden="true">
            ${this.content.catalog.studies.filter((_, index) => index % 3 === 0).map((study, index) => html`<i style="--x:${8 + (index * 17) % 84}%;--y:${15 + (index * 29) % 70}%;--c:${this.content.catalog.techniques.find((tech) => tech.slug === study.techniques[0])?.color}"></i>`)}
          </div>
          <div class="callout-copy">
            <span class="section-number">02</span>
            <h2>Follow the beat,<br>not the genre label.</h2>
            <p>The graph connects performances when a recognizable rhythmic idea is inherited, transformed, or recombined. Technique comes first; metal history falls out around it.</p>
            <a class="text-link" href="/graph">Open the evolutionary graph →</a>
          </div>
        </section>

        <section class="section current-studies">
          <div class="section-heading"><div><span class="section-number">03</span><h2>Playable studies</h2></div><p>Exact instrumental excerpts with controlled drummer substitutions.</p></div>
          <div class="study-grid">
            ${playable.map((study) => html`
              <a class="study-card featured" href="/studies/${study.slug}">
                <span class="study-year">${study.year}</span><h3>${study.band}</h3><strong>${study.track}</strong><p>${study.focus}</p><span class="text-link">Open study →</span>
              </a>
            `)}
          </div>
        </section>
      </main>
    `;
  }
}
